import React, { useEffect, useState } from 'react';
import { Button, Dropdown, Menu, Modal } from 'antd';
import { EditOutlined, MoreOutlined } from '@ant-design/icons';
import { useTranslation } from 'react-i18next';
import MatchNoteForm from './MatchNoteForm';
import { matchNoteRequest } from '../../lib/matchNoteApi';
import { MIN_ADMIN_YETKI } from '../../shared/variables/routes';
import { MATCH_NOTE_WINDOW_MS } from '../../shared/utils/league-match-note';
import { showNotification } from '../../shared/utils/notifications';

const DAY_MS = 24 * 60 * 60 * 1000;

type Props = {
  event: LeagueFeed.Event;
  viewer?: Users.User;
  // eslint-disable-next-line no-unused-vars -- render callback signature
  renderName: (player?: LeagueFeed.Player) => React.ReactNode;
  // "Maçını anlat" satırı (FeedMatchStory) not ekleme düğmesini taşır; her artışta yazma açılır.
  editSignal?: number;
  // eslint-disable-next-line no-unused-vars -- callback type signature
  onNotesChange?: (notes: LeagueFeed.Note[]) => void;
};

/**
 * Maç sonucu kartının açıklamaları: oyuncu notları (kazananınki önce). Maçın oyuncusu onaydan
 * sonraki 4 gün içinde kendi notunu ekler/düzenler; kart bunu hatırlatır. Diğerleri şikâyet eder.
 */
export default function MatchNotes({
  event,
  viewer,
  renderName,
  editSignal,
  onNotesChange,
}: Props) {
  const { t } = useTranslation('leagues');
  const [notes, setNotes] = useState<LeagueFeed.Note[]>(event.veri.notlar || []);
  const [editing, setEditing] = useState(false);
  const [reported, setReported] = useState<string[]>([]);
  useEffect(() => setNotes(event.veri.notlar || []), [event.veri.notlar]);
  useEffect(() => {
    if (editSignal) setEditing(true);
  }, [editSignal]);
  const { veri } = event;
  const players = [veri.kazanan, veri.kaybeden];
  const matchId = veri.mac_id;
  if (!matchId) return null;
  const participant = !!viewer?.id && players.some(player => player?.id === viewer.id);
  const remaining = event.olusturma + MATCH_NOTE_WINDOW_MS - Date.now();
  const open = participant && remaining > 0;
  const own = notes.find(note => note.oyuncu_id === viewer?.id) || null;
  const admin = (viewer?.yetki || 0) >= MIN_ADMIN_YETKI;

  const saved = (text: string | null) => {
    setEditing(false);
    setNotes(current => {
      const others = current.filter(note => note.oyuncu_id !== viewer?.id);
      const next = text ? [...others, { oyuncu_id: viewer!.id, metin: text }] : others;
      // Kazananın notu önce.
      const sorted = next.sort(
        (a, b) =>
          Number(b.oyuncu_id === veri.kazanan?.id) - Number(a.oyuncu_id === veri.kazanan?.id),
      );
      onNotesChange?.(sorted);
      return sorted;
    });
    showNotification('success', t(text ? 'detail.notes.saved' : 'detail.notes.removed'));
  };

  const moderate = (note: LeagueFeed.Note, action: 'report' | 'remove') =>
    Modal.confirm({
      title: t(
        action === 'report' ? 'detail.notes.reportConfirm' : 'detail.notes.adminRemoveConfirm',
      ),
      okText: t(action === 'report' ? 'detail.notes.report' : 'detail.notes.remove'),
      cancelText: t('detail.notes.cancel'),
      okButtonProps: { danger: action === 'remove' },
      onOk: async () => {
        try {
          if (action === 'report') {
            await matchNoteRequest(matchId, 'POST', { author: note.oyuncu_id, report: true });
            setReported(current => [...current, note.oyuncu_id]);
            showNotification('success', t('detail.notes.reported'));
          } else {
            await matchNoteRequest(matchId, 'DELETE', { author: note.oyuncu_id });
            setNotes(current => current.filter(item => item.oyuncu_id !== note.oyuncu_id));
            showNotification('success', t('detail.notes.removed'));
          }
        } catch {
          showNotification(
            'error',
            t(action === 'report' ? 'detail.notes.reportError' : 'detail.notes.removeError'),
          );
        }
      },
    });

  const menu = (note: LeagueFeed.Note) => {
    if (!viewer?.id || note.oyuncu_id === viewer.id) return null;
    const items = [
      !reported.includes(note.oyuncu_id) && (
        <Menu.Item key="report" onClick={() => moderate(note, 'report')}>
          {t('detail.notes.report')}
        </Menu.Item>
      ),
      admin && (
        <Menu.Item key="remove" danger onClick={() => moderate(note, 'remove')}>
          {t('detail.notes.remove')}
        </Menu.Item>
      ),
    ].filter(Boolean);
    if (!items.length) return null;
    return (
      <Dropdown overlay={<Menu>{items}</Menu>} trigger={['click']} placement="bottomRight">
        <Button
          type="text"
          className="match-notes__more"
          icon={<MoreOutlined aria-hidden="true" />}
          aria-label={t('detail.notes.options')}
          title={t('detail.notes.options')}
        />
      </Dropdown>
    );
  };

  const days = Math.max(1, Math.ceil(remaining / DAY_MS));

  return (
    <div className="match-notes">
      {notes.length > 0 && (
        <ul className="match-notes__list" aria-label={t('detail.notes.title')}>
          {notes.map(note => {
            const author = players.find(player => player?.id === note.oyuncu_id);
            const mine = note.oyuncu_id === viewer?.id;
            if (mine && editing) return null;
            return (
              <li key={note.oyuncu_id} className="match-notes__item">
                <p>
                  {renderName(author)} <span className="match-notes__text">{note.metin}</span>
                </p>
                {mine && open && (
                  <Button
                    type="text"
                    className="match-notes__edit"
                    onClick={() => setEditing(true)}
                    icon={<EditOutlined aria-hidden="true" />}
                    aria-label={t('detail.notes.edit')}
                    title={t('detail.notes.edit')}
                  />
                )}
                {menu(note)}
              </li>
            );
          })}
        </ul>
      )}
      {open && editing && (
        <MatchNoteForm
          matchId={matchId}
          initial={own?.metin || null}
          onSaved={saved}
          onCancel={() => setEditing(false)}
          autoFocus
        />
      )}
      {/* Not ekleme ve rakipten isteme akışta "Maçını anlat" satırında (FeedMatchStory). */}
      {open && !own && !editing && editSignal === undefined && (
        <Button
          className="match-notes__add"
          icon={<EditOutlined aria-hidden="true" />}
          onClick={() => setEditing(true)}
        >
          {t('detail.notes.add', { count: days })}
        </Button>
      )}
    </div>
  );
}

MatchNotes.defaultProps = { viewer: undefined, editSignal: undefined, onNotesChange: undefined };
